import { ArrowRight, Download } from 'lucide-react';
import { useMemo, useState } from 'react';
import { CreatorActivity } from '../components/CreatorActivity';
import { CreatorLink } from '../components/CreatorLink';
import { ConnectionBadge } from '../components/ConnectionBadge';
import { DeviceIcon } from '../components/DeviceIcon';
import { DevicePicker } from '../components/DevicePicker';
import { ProfileTable } from '../components/ProfileTable';
import { SearchForm } from '../components/SearchForm';
import { Link } from '../components/Link';
import { catalog, creatorsBySlug, newestProfiles, topPacks, topProfiles } from '../lib/catalog';
import { activityForCreators } from '../lib/creatorFeed';
import { downloadCountForPack, downloadCountForProfile, formatDownloads } from '../lib/downloadCounts';
import { formatDate, packPath, profilePath } from '../lib/navigation';

interface HomePageProps {
  notify: (message: string, tone?: 'success' | 'error') => void;
  followedCreators: string[];
}

export function HomePage({ notify, followedCreators }: HomePageProps) {
  const [popularDevice, setPopularDevice] = useState('');
  const followed = useMemo(() => followedCreators.flatMap((slug) => {
    const creator = creatorsBySlug.get(slug);
    return creator ? [creator] : [];
  }), [followedCreators]);
  const followingActivity = useMemo(() => activityForCreators(followed), [followed]);
  const popularProfiles = topProfiles(10, popularDevice);
  const popularPacks = topPacks();
  const recentProfiles = newestProfiles();
  const totalDownloads = catalog.profiles.reduce((total, profile) => total + downloadCountForProfile(profile), 0);

  return (
    <>
      <section className="archive-hero">
        <div className="archive-hero-top">
          <div className="archive-hero-copy">
            <h1>Retro Profiles</h1>
            <p>
              A community hub for sharing video scaler profiles. Search, view, and download profiles and profile packs
              for your devices.
            </p>
          </div>
        </div>
        <div className="archive-search">
          <SearchForm />
        </div>
        <dl className="archive-summary">
          <div><dt>Profiles</dt><dd>{formatDownloads(catalog.profileCount)}</dd></div>
          <div><dt>Profile packs</dt><dd>{formatDownloads(catalog.packCount)}</dd></div>
          <div><dt>Devices and collections</dt><dd>{catalog.devices.length}</dd></div>
          <div><dt>Total downloads</dt><dd>{formatDownloads(totalDownloads)}</dd></div>
        </dl>
      </section>

      <CreatorActivity items={followingActivity} returnTo="/" variant="following" />

      <section className="content-section home-section" aria-labelledby="popular-profiles-heading">
        <div className="section-heading">
          <div><h2 id="popular-profiles-heading">Popular Profiles</h2></div>
          <Link className="text-link" href={popularDevice ? `/search?device=${encodeURIComponent(popularDevice)}` : '/search'}>Browse all <ArrowRight size={15} /></Link>
        </div>
        <div className="popular-profiles-toolbar">
          <DevicePicker value={popularDevice} onChange={setPopularDevice} />
          <p role="status">{popularProfiles.length} most downloaded {popularProfiles.length === 1 ? 'profile' : 'profiles'}{popularDevice ? '' : ' across all devices'}</p>
          {popularDevice && <button className="text-button" type="button" onClick={() => setPopularDevice('')}>Clear filter</button>}
        </div>
        <ProfileTable profiles={popularProfiles} notify={notify} compact />
      </section>

      <section className="content-section home-section" id="packs">
        <div className="section-heading">
          <div><h2>Top Profile Packs</h2></div>
          <Link className="text-link" href="/packs">View every pack <ArrowRight size={15} /></Link>
        </div>
        <div className="pack-grid">
          {popularPacks.map((pack, index) => (
            <article className="pack-card" key={pack.id}>
              <div className="pack-rank">{String(index + 1).padStart(2, '0')}</div>
              <DeviceIcon device={pack.device} compact />
              <div className="pack-card-body">
                <p className="pack-device">{pack.device}</p>
                <h3><Link href={packPath(pack)}>{pack.name}</Link></h3>
                <p><CreatorLink name={pack.creator} /> · {pack.profileCount} profiles</p>
                <div className="tag-row">
                  {pack.connections.slice(0, 3).map((connection) => <ConnectionBadge key={connection} connection={connection} />)}
                </div>
              </div>
              <div className="pack-card-meta">
                <span><Download size={14} />{formatDownloads(downloadCountForPack(pack))}</span>
                <Link className="icon-button subtle" href={packPath(pack)} aria-label={`Open ${pack.name}`}><ArrowRight size={17} /></Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section home-section">
        <div className="section-heading">
          <div><h2>Newly Uploaded Profiles</h2></div>
        </div>
        <div className="recent-grid">
          {recentProfiles.map((profile) => (
            <article className="recent-card" key={profile.id}>
              <DeviceIcon device={profile.device} compact />
              <div>
                <p className="recent-device">{profile.device}</p>
                <h3><Link href={profilePath(profile)}>{profile.name}</Link></h3>
                <ConnectionBadge connection={profile.connection} />
              </div>
              <time dateTime={profile.uploadedAt}>{formatDate(profile.uploadedAt)}</time>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}
